import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useContent } from '../hooks/useContent';

const Contact = () => {
  useEffect(() => { window.scrollTo(0,0); }, []);
  const { t } = useContent('contact');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', wealth: '', message: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    // mock submission - save to localStorage
    const submissions = JSON.parse(localStorage.getItem('stp_submissions') || '[]');
    submissions.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('stp_submissions', JSON.stringify(submissions));
    setSubmitted(true);
  };

  return (
    <main className="pt-32 pb-24">
      <section className="max-w-[1440px] mx-auto px-8 md:px-14 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-8">{t('hero.kicker')}</p>
            <h1 className="font-serif text-6xl md:text-7xl text-navy leading-[0.95]">
              {t('hero.title_line1')}<br /><span className="italic">{t('hero.title_line2')}</span>
            </h1>
            <p className="font-serif text-xl text-navy/80 leading-snug mt-8 max-w-lg">
              {t('hero.body')}
            </p>

            <div className="mt-16 space-y-10" data-testid="contact-offices">
              {(t('offices') || []).map((o, i) => <ContactBlock key={i} {...o} />)}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-cream border border-navy/15 p-8 md:p-12">
              {submitted ? (
                <div className="py-24 text-center">
                  <div className="w-14 h-14 rounded-full bg-navy text-cream flex items-center justify-center mx-auto">
                    <Check className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif text-4xl text-navy mt-8">{t('thanks.title')}</h3>
                  <p className="font-sans text-navy/70 mt-4 max-w-md mx-auto">{t('thanks.body')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="font-serif text-3xl text-navy mb-8">{t('form.title')}</h2>
                  <Field label={t('form.name')} name="name" value={form.name} onChange={handleChange} required />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label={t('form.email')} type="email" name="email" value={form.email} onChange={handleChange} required />
                    <Field label={t('form.phone')} name="phone" value={form.phone} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-2">{t('form.wealth')}</label>
                    <select
                      name="wealth"
                      value={form.wealth}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-navy/30 py-3 font-serif text-lg text-navy focus:outline-none focus:border-navy transition-colors"
                    >
                      <option value="">{t('form.wealth_placeholder')}</option>
                      {(t('form.wealth_options') || []).map((o, i) => <option key={i}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-2">{t('form.message')}</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      className="w-full bg-transparent border-b border-navy/30 py-3 font-serif text-lg text-navy focus:outline-none focus:border-navy transition-colors resize-none"
                      placeholder={t('form.message_placeholder')}
                    />
                  </div>
                  <button
                    type="submit"
                    className="mt-6 font-sans text-[11px] tracking-[0.3em] text-cream px-10 py-4 bg-navy hover:bg-navydark transition-colors"
                  >
                    {t('form.submit')}
                  </button>
                  <p className="font-sans text-xs text-navy/50 mt-4">{t('form.consent')}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, name, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-2">{(label || '').toUpperCase()}{required && ' *'}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-navy/30 py-3 font-serif text-lg text-navy focus:outline-none focus:border-navy transition-colors"
    />
  </div>
);

const ContactBlock = ({ office, address, city, email, phone, contact }) => (
  <div className="border-l-2 border-navy pl-6">
    <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-2">{(office || '').toUpperCase()}</p>
    <p className="font-serif text-2xl text-navy">{address}</p>
    <p className="font-serif text-lg text-navy/70">{city}</p>
    <div className="mt-4 space-y-1">
      <a href={`mailto:${email}`} className="block font-sans text-sm text-navy hover:opacity-70">{email}</a>
      <p className="font-sans text-sm text-navy/70">{phone}</p>
      <p className="font-sans text-xs text-navy/60 mt-2 italic">{contact}</p>
    </div>
  </div>
);

export default Contact;
